import type { PresetValue } from '@/lib/constants/time-presets';
import posthog from 'posthog-js';

// Typed PostHog event catalog (CL-321) — the tracking plan as code: compiler-enforced
// names + props, and a PII allowlist by construction. Convention: camelCase,
// object+action, past tense. NEVER pass query text / PII. The current `screen`, id
// context, `appVersion` and `environment` are added automatically by the before_send
// hook (enrich.ts) — don't duplicate them here.

type FilterType = 'tag' | 'status' | 'country';
type ExportFormat = 'csv' | 'json';
type DeviceOpenSource = 'mapSpotlight' | 'devicesTable';
type VesselOpenSource = 'mapSpotlight' | 'fleetSearch';
type DemoCardSource = 'signIn' | 'signUp';
type AiFilterEntity = 'bookings' | 'devices';
type AiFilterMatch = 'all' | 'any';
type AiFilterRefusal = 'no_fields' | 'not_a_filter' | 'unsafe';
type AiFilterFailure = 'no_key' | 'timeout' | 'no_output' | 'rate_limited' | 'error';

export const analytics = {
  // Navigation / consumption — how customers reach and browse devices
  deviceSearched: (p: { resultCount: number }) => posthog.capture('deviceSearched', p),
  deviceOpened: (p: { source: DeviceOpenSource }) => posthog.capture('deviceOpened', p),
  mapDeviceClicked: () => posthog.capture('mapDeviceClicked'),
  mapVesselClicked: () => posthog.capture('mapVesselClicked'),
  vesselOpened: (p: { source: VesselOpenSource }) => posthog.capture('vesselOpened', p),
  filterApplied: (p: { filterType: FilterType }) => posthog.capture('filterApplied', p),
  reportExported: (p: { format: ExportFormat; section: string; rowCount: number }) =>
    posthog.capture('reportExported', p),

  // Device-detail engagement — what information customers look at
  deviceDetailTabViewed: (p: { tab: string }) => posthog.capture('deviceDetailTabViewed', p),
  deviceChartSeriesToggled: (p: { series: string; visible: boolean }) =>
    posthog.capture('deviceChartSeriesToggled', p),
  deviceChartPresetSelected: (p: { preset: PresetValue }) =>
    posthog.capture('deviceChartPresetSelected', p),

  // Acquisition — prospects hitting the auth pages (99% of /sign-in traffic has no account)
  demoCardClicked: (p: { source: DemoCardSource }) => posthog.capture('demoCardClicked', p),

  // Ask Balder AI - what the user typed rides the PostHog AI trace, never these
  aiFilterAsked: (p: { entity: AiFilterEntity; questionLength: number; wordCount: number }) =>
    posthog.capture('aiFilterAsked', p),
  aiFilterApplied: (p: {
    entity: AiFilterEntity;
    conditionCount: number;
    ignoredCount: number;
    match: AiFilterMatch;
    latencyMs: number;
  }) => posthog.capture('aiFilterApplied', p),
  aiFilterRefused: (p: { entity: AiFilterEntity; kind: AiFilterRefusal }) =>
    posthog.capture('aiFilterRefused', p),
  aiFilterFailed: (p: { entity: AiFilterEntity; reason: AiFilterFailure }) =>
    posthog.capture('aiFilterFailed', p),
  aiFilterChipEdited: (p: { entity: AiFilterEntity; field: string; secondsSinceApply: number }) =>
    posthog.capture('aiFilterChipEdited', p),
  aiFilterUndone: (p: { entity: AiFilterEntity; secondsSinceApply: number }) =>
    posthog.capture('aiFilterUndone', p),
};
